import { useQueryClient } from "@tanstack/react-query";
import {
  useGetAddresses,
  useAddAddress,
  useUpdateAddress,
  useDeleteAddress,
} from "@/api/hooks/use-query-api";
import { useAddressesStore } from "../store/addresses-store";
import { useToast } from "@/features/toast";
import { useTranslation } from "react-i18next";
import { QueryKeys } from "@/api/config/query-keys";
import type { Address } from "@/api/models/api-types";

export const useAddresses = () => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();

  // API Hooks
  const { data: addressesResponse, isLoading, isError, error } = useGetAddresses();
  const addAddressMutation = useAddAddress();
  const updateAddressMutation = useUpdateAddress();
  const deleteAddressMutation = useDeleteAddress();

  // Store
  const {
    isDeleteAddressDialogOpen,
    isEditAddressDialogOpen,
    editingAddressId,
    deletingAddressId,
    openDeleteAddressDialog,
    closeDeleteAddressDialog,
    openEditAddressDialog,
    closeEditAddressDialog,
  } = useAddressesStore();

  // Extract addresses
  const addresses = addressesResponse?.data || [];

  // Get editing address
  const editingAddress = addresses.find((a) => a._id === editingAddressId);
  const deletingAddress = addresses.find((a) => a._id === deletingAddressId);

  // Add Address
  const addAddress = async (addressData: Omit<Address, "_id">) => {
    try {
      await addAddressMutation.mutateAsync(addressData);
      await queryClient.invalidateQueries({ queryKey: QueryKeys.ADDRESS.all });
      success(t("addresses.addSuccess"));
      closeEditAddressDialog();
    } catch (apiError: any) {
      showError(apiError?.message || t("addresses.addError"));
    }
  };

  // Update Address
  const updateAddress = async (addressId: string, addressData: Partial<Address>) => {
    try {
      await updateAddressMutation.mutateAsync({ id: addressId, addressData });
      await queryClient.invalidateQueries({ queryKey: QueryKeys.ADDRESS.all });
      success(t("addresses.updateSuccess"));
      closeEditAddressDialog();
    } catch (apiError: any) {
      showError(apiError?.message || t("addresses.updateError"));
    }
  };

  // Delete Address
  const deleteAddress = async () => {
    if (!deletingAddressId) return;

    try {
      await deleteAddressMutation.mutateAsync(deletingAddressId);
      await queryClient.invalidateQueries({ queryKey: QueryKeys.ADDRESS.all });
      success(t("addresses.deleteSuccess"));
      closeDeleteAddressDialog();
    } catch (apiError: any) {
      showError(apiError?.message || t("addresses.deleteError"));
    }
  };

  // Save Address (Add or Update)
  const saveAddress = async (addressData: any, addressId?: string) => {
    if (addressId) {
      await updateAddress(addressId, addressData);
    } else {
      await addAddress(addressData);
    }
  };

  return {
    // Data
    addresses,
    editingAddress,
    deletingAddress,

    // States
    isLoading,
    isError,
    error,
    isDeleting: deleteAddressMutation.isPending,
    isSaving: addAddressMutation.isPending || updateAddressMutation.isPending,

    // Modal States
    isDeleteAddressDialogOpen,
    isEditAddressDialogOpen,

    // Actions
    addAddress,
    updateAddress,
    deleteAddress,
    saveAddress,

    // Modal Actions
    openDeleteAddressDialog,
    closeDeleteAddressDialog,
    openEditAddressDialog,
    closeEditAddressDialog,
  };
};

